import type { FC } from 'react'

/*
  Shared "Connect to what you actually need" avatar section.

  Used verbatim on the home, features and pricing pages. The animation is the
  Webflow IX2 interaction a-20 ("Company Avatars"): each `.company-avatar-circle`
  scales 0 -> 1 with a random stagger as it scrolls into view, then runs a gentle
  infinite pulse. That, plus the `.fade-in-move-on-scroll` heading reveal, is
  driven globally (and identically per route) by `use-site-animations.ts` — no
  per-instance JS here, so every page gets the exact same animation.

  Only the per-avatar Webflow grid-placement ids (`w-node-...`, referenced by
  `place-self` rules in site.css) differ between pages, so those are passed in.
  The visual config (class, image, wrap modifier) is shared here.
*/

interface AvatarConfig {
    cls: string
    src: string
    wrap?: string
}

interface AvatarItem extends AvatarConfig {
    id: string
}

interface ConnectSectionProps {
    /** Page-specific Webflow grid-placement ids for the top row (10 items). */
    topIds: string[]
    /** Page-specific Webflow grid-placement ids for the bottom row (10 items). */
    bottomIds: string[]
    /** Heading wrapper class — varies slightly per page in the export. */
    headingClassName?: string
    heading?: string
}

const TOP_AVATARS: AvatarConfig[] = [
    { cls: '_01', src: '/images/Company-Avatar-02.svg' },
    { cls: '_02', src: '/images/Company-Avatar-05.svg' },
    { cls: '_03', src: '/images/Company-Avatar-06.svg', wrap: 'bot' },
    { cls: '_04', src: '/images/Company-Avatar-01.svg', wrap: 'up' },
    { cls: '_05', src: '/images/Company-Avatar-07.svg' },
    { cls: '_06', src: '/images/Company-Avatar-04.svg', wrap: 'random' },
    { cls: '_07', src: '/images/Company-Avatar-03.svg', wrap: 'random' },
    { cls: '_08', src: '/images/Company-Avatar-08.svg', wrap: 'bot' },
    { cls: '_09', src: '/images/Company-Avatar-15.svg', wrap: 'bot' },
    { cls: '_10', src: '/images/Company-Avatar-18.svg', wrap: 'up hide' }
]

const BOTTOM_AVATARS: AvatarConfig[] = [
    { cls: '_11', src: '/images/Company-Avatar-17.svg' },
    { cls: '_12', src: '/images/Company-Avatar-18.svg', wrap: 'bot' },
    { cls: '_13', src: '/images/Company-Avatar-19.svg', wrap: 'bot' },
    { cls: '_14', src: '/images/Company-Avatar-20.svg', wrap: 'up' },
    { cls: '_15', src: '/images/Company-Avatar-09.svg' },
    { cls: '_16', src: '/images/Company-Avatar-10.svg' },
    { cls: '_17', src: '/images/Company-Avatar-11.svg', wrap: 'random' },
    { cls: '_18', src: '/images/Company-Avatar-12.svg' },
    { cls: '_19', src: '/images/Company-Avatar-14.svg' },
    { cls: '_20', src: '/images/Company-Avatar-13.svg', wrap: 'up hide' }
]

const zip = (configs: AvatarConfig[], ids: string[]): AvatarItem[] =>
    configs.map((cfg, i) => ({ ...cfg, id: ids[i] }))

const Avatar: FC<{ item: AvatarItem }> = ({ item }) => (
    <div id={item.id} className={`company-avatar-container${item.wrap ? ` ${item.wrap}` : ''}`}>
        <div className={`company-avatar-circle ${item.cls}`}>
            <img src={item.src} loading='lazy' alt='' className='company-avatar' />
        </div>
        <div className='grey-circle-holder'>
            <div className='gray-circle' />
        </div>
    </div>
)

export const ConnectSection: FC<ConnectSectionProps> = ({
    topIds,
    bottomIds,
    headingClassName = 'center-text',
    heading = 'Connect to what you actually need'
}) => (
    <div className='section'>
        <div className='container _6em-bottom'>
            <div className={headingClassName}>
                <div className='fade-in-move-on-scroll'>
                    <h2>{heading}</h2>
                </div>
            </div>
            <div className='connect-section-container'>
                <div className='w-layout-grid connect-grid-container'>
                    {zip(TOP_AVATARS, topIds).map((item) => (
                        <Avatar key={item.id} item={item} />
                    ))}
                </div>
                <div className='w-layout-grid connect-grid-container'>
                    {zip(BOTTOM_AVATARS, bottomIds).map((item) => (
                        <Avatar key={item.id} item={item} />
                    ))}
                </div>
            </div>
        </div>
    </div>
)

export default ConnectSection
